/* eslint-disable promise/prefer-await-to-then */
import {
	Calendar,
	ChoiceGroup,
	DateRangeType,
	DefaultButton,
	defaultCalendarStrings,
	DialogFooter,
	IChoiceGroupOption,
	Label,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	ProgressIndicator,
	Stack
} from "@fluentui/react"
import { useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { IS_ADMIN, safeFormatDateValue } from "../lib/helper"
import { downloadBase64Content } from "../services/helperService"
import { IAccount } from "../types/IAccount"
import { IPlanSelectorPlan } from "../types/IGroupPlan"
import { PlanSelector } from "./planSelector"

type PlanDocumentsOutput = {
	planId: string
	documentType: "SOB" | "SBC"
	documentContent: string
}

type sobOrSBC = "SOB" | "SBC"

type IDocumentPayload = {
	effectiveDate: string
	groupName: string
	plans: string[]
	groupCode: string
	documentType: sobOrSBC
}

export function SOBGenerator() {
	const [effectiveDate, setEffectiveDate] = useState<string>(safeFormatDateValue(new Date(), "M/1/yyyy"))
	const [loadingGroups, setLoadingGroups] = useState<boolean>(true)
	const [group, setGroup] = useState<IAccount>()
	const [groups, setGroups] = useState<IAccount[]>()
	const [payLoad, setPayLoad] = useState<IDocumentPayload>()
	const [showPlanSelector, setShowPlanSelector] = useState<boolean>(false)
	const [selectedPlans, setSelectedPlans] = useState<IPlanSelectorPlan[]>()
	const [sobOrSBC, setSOBOrSBC] = useState<sobOrSBC>("SOB")
	const groupsQuery = IS_ADMIN ? "/getGroupsForBroker" : "getGroupsForBroker?accountType=Prospect"

	useEffect(() => {
		if (!isSubmitDisabled()) {
			setPayLoad(getSOBDocumentPayload())
		}
	}, [effectiveDate, selectedPlans, sobOrSBC])

	// useEffect(() => {
	// 	setLoadingGroups(true)
	// 	makeApiCall(groupsQuery)
	// 		.then((response: IAccount[]) => {
	// 			const accounts = response.filter((value, index, self) => index === self.findIndex(t => t.accountid === value.accountid))
	// 			setGroups(accounts)
	// 			setLoadingGroups(false)
	// 		})
	// 		.catch(err => {
	// 			console.error("Error getting groups", err)
	// 			setLoadingGroups(false)
	// 		})
	// }, [])

	const getSOBDocumentPayload = (): IDocumentPayload => {
		if (effectiveDate && selectedPlans && sobOrSBC) {
			return {
				effectiveDate: effectiveDate,
				groupName: "ACMG",
				plans: selectedPlans.map(plan => plan.id),
				groupCode: "",
				documentType: sobOrSBC
			}
		}
		return undefined
	}

	const {
		data: sobDocuments,
		loading: loadingDocs,
		error: errorGettingDocs,
		fetchData: generateDocs,
		setData: setDocs,
		abort: abortDocs
	} = useApiCall<PlanDocumentsOutput[]>("/getPlanDocuments", { handlerOnly: true, body: JSON.stringify(payLoad), method: "POST" })

	const isSubmitDisabled = () => {
		return loadingDocs || !effectiveDate || !selectedPlans || selectedPlans.length === 0
	}

	const downloadDocuments = () => {
		if (sobDocuments && sobDocuments.length > 0) {
			for (const sobDocument of sobDocuments) {
				const planName = selectedPlans.find(p => p.id === sobDocument.planId)?.name
				downloadBase64Content(sobDocument.documentContent, `${planName} - ${sobDocument.documentType}.pdf`, "application/pdf")
			}
		}
	}

	return (
		<div style={{ maxWidth: 740 }}>
			<Stack tokens={{ childrenGap: 8 }}>
				{/* <Dropdown
					disabled={loadingGroups}
					options={groups?.map(g => {
						const isProspect = g.cw_accounttype.includes("Prospect")
						return { key: g.accountid, text: `${g.name} (${isProspect ? "Prospect" : "Customer"})` }
					})}
					label="Group"
					placeholder={loadingGroups ? "Loading groups..." : "Select a group"}
					selectedKey={group?.accountid}
					onChange={(_, option) => setGroup(groups.find(g => g.accountid === option.key))}
				/> */}
				<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
					<Stack>
						<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
							<Label>Select an effective date by clicking on the month below:</Label>
							<span>{safeFormatDateValue(effectiveDate, "M/1/yyyy")}</span>
						</Stack>

						<Calendar
							dateRangeType={DateRangeType.Month}
							showGoToToday
							highlightSelectedMonth
							minDate={new Date()}
							isDayPickerVisible={false}
							onSelectDate={date => {
								const firstDay = new Date(date.getFullYear(), date.getMonth(), 1)
								setEffectiveDate(safeFormatDateValue(firstDay, "M/1/yyyy"))
							}}
							//value={effectiveDate ? new Date(effectiveDate.replace(/.$/, "2")) : undefined} //if you don't replace the final character, the date evaulates incorrectly to the previous month
							strings={defaultCalendarStrings}
						/>
					</Stack>
				</Stack>
				<Stack>
					<ChoiceGroup
						label="Choose either Summary of Benefits and Coverage (SBC) or Summary of Benefits (SOB)"
						selectedKey={sobOrSBC}
						onChange={(_ev, option: IChoiceGroupOption) => {
							setSOBOrSBC(option.key as sobOrSBC)
						}}
						options={[
							{ key: "SOB", text: "SOB" },
							{ key: "SBC", text: "SBC" }
						]}
					/>
				</Stack>
				<Stack tokens={{ childrenGap: 4 }} verticalAlign="center">
					<Label>Click "Select Plans" below to choose which plan documents to generate</Label>
					{selectedPlans && selectedPlans.length > 0 ? (
						<PlanSelector
							onDismiss={() => {
								/*void*/
							}}
							onSave={plans => {
								/*void*/
							}}
							defaultSelectedPlans={selectedPlans.map(p => p.name) || []}
							displayOnly
							onlyShowPlans={selectedPlans.map(p => p.name)}
						/>
					) : (
						"None"
					)}
				</Stack>
				{!loadingDocs && (
					<PrimaryButton
						text="Select Plans"
						onClick={() => {
							setShowPlanSelector(true)
						}}
					/>
				)}
				{loadingDocs && <ProgressIndicator label={`Generating ${selectedPlans.length} ${sobOrSBC} Documents`} />}
				{errorGettingDocs && <MessageBar messageBarType={MessageBarType.error}>There was an error generating the documents</MessageBar>}
			</Stack>

			<DialogFooter>
				{payLoad && !loadingDocs && sobDocuments && <MessageBar messageBarType={MessageBarType.success}>Documents generated. Click "Download Documents" to download them.</MessageBar>}
				{payLoad && !loadingDocs && sobDocuments && (
					<PrimaryButton styles={{ root: { background: "green" } }} text="Download Documents" iconProps={{ iconName: "Download" }} onClick={downloadDocuments} />
				)}
				{loadingDocs && (
					<DefaultButton
						onClick={() => {
							setDocs(undefined)
							abortDocs()
						}}
						text="Cancel"
					/>
				)}
				<PrimaryButton
					onClick={() => {
						setDocs(undefined)
						generateDocs()
					}}
					text="Generate Documents"
					disabled={isSubmitDisabled()}
				/>
			</DialogFooter>
			{showPlanSelector && (
				<PlanSelector
					displayOnly={false}
					onDismiss={() => setShowPlanSelector(false)}
					onSave={plans => {
						setSelectedPlans(plans)
						setShowPlanSelector(false)
					}}
					defaultSelectedPlans={selectedPlans?.map(p => p.name) || []}
				/>
			)}
		</div>
	)
}
